/**
 * Centralized WhatsApp link + click tracking.
 *
 * Single source of truth for the business WhatsApp number, default
 * prefilled message, and analytics event so every CTA across the site
 * stays consistent.
 */

// Full international format, digits only (no +, spaces, or dashes).
export const WHATSAPP_NUMBER = "50769325719";

/** Default messages by audience context. */
export const WHATSAPP_MESSAGES = {
  buyer:
    "Hi Switzh — I'm looking for pricing and availability.\n\nProduct: \nQuantity: \nDelivery location: \n\nPlease include full landed price (DDP) and delivery timeline.",
  supplier:
    "Hi Switzh — I'm reaching out as a potential supply partner. I'd like to learn more about partnering with your platform.",
} as const;

const DEFAULT_MESSAGE = WHATSAPP_MESSAGES.buyer;

/** Routes that should default to the supplier/partner message. */
const SUPPLIER_PATH_PREFIXES = [
  "/supplier-partners",
  "/supplier-login",
  "/supplier-portal",
];

/**
 * Resolve the contextual default message based on a pathname.
 * Supplier-facing pages get the partner message; everything else gets buyer.
 */
export const getDefaultMessageForPath = (pathname?: string | null): string => {
  if (!pathname) return WHATSAPP_MESSAGES.buyer;
  // Strip locale prefix (/es, /pt) if present.
  const stripped = pathname.replace(/^\/(es|pt)(?=\/|$)/, "") || "/";
  const isSupplier = SUPPLIER_PATH_PREFIXES.some(
    (prefix) => stripped === prefix || stripped.startsWith(`${prefix}/`),
  );
  return isSupplier ? WHATSAPP_MESSAGES.supplier : WHATSAPP_MESSAGES.buyer;
};

/**
 * Build a wa.me link with a URL-encoded prefilled message.
 * Works on mobile (opens the WhatsApp app) and desktop (WhatsApp Web).
 */
export const buildWhatsAppUrl = (message: string = DEFAULT_MESSAGE) => {
  const text = encodeURIComponent(message || DEFAULT_MESSAGE);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
};

/** Default WhatsApp link used by most CTAs. */
export const WHATSAPP_URL = buildWhatsAppUrl();

/**
 * Build a wa.me link addressed to a SPECIFIC phone number (e.g. messaging
 * a customer back from the admin panel). Strips non-digits — wa.me requires
 * digits only, in international format.
 *
 * Returns null if the phone is missing or doesn't contain enough digits to
 * be a plausible international number.
 */
export const buildWhatsAppUrlToCustomer = (
  phone: string | null | undefined,
  message: string,
): string | null => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 8) return null;
  const text = encodeURIComponent(message);
  return `https://wa.me/${digits}?text=${text}`;
};

/**
 * Track a WhatsApp click. Pushes a `whatsapp_click` event into the
 * dataLayer (GA4 / GTM friendly) and falls back to a console log so
 * developers can verify locally.
 */
import { recordEvent } from "@/lib/analytics";

export const trackWhatsAppClick = (location: string) => {
  try {
    type GtagWindow = Window & {
      dataLayer?: Array<Record<string, unknown> | IArguments>;
      gtag?: (...args: unknown[]) => void;
    };
    const w = window as GtagWindow;
    w.dataLayer = w.dataLayer || [];
    const payload = {
      location,
      button_text: "Send RFQ via WhatsApp",
      whatsapp_number: WHATSAPP_NUMBER,
      page_location:
        typeof window !== "undefined" ? window.location.href : undefined,
      page_title:
        typeof document !== "undefined" ? document.title : undefined,
      timestamp: new Date().toISOString(),
    };
    // GA4 event (also pushed to dataLayer for GTM compatibility)
    if (typeof w.gtag === "function") {
      w.gtag("event", "whatsapp_click", payload);
    } else {
      (w.dataLayer as Array<Record<string, unknown>>).push({
        event: "whatsapp_click",
        ...payload,
      });
    }
    // First-party mirror so the event also appears in analytics_events
    void recordEvent("whatsapp_click", { metadata: payload });
  } catch {
    // no-op: tracking should never break navigation
  }
};

/**
 * Standard props for any anchor that opens WhatsApp.
 * If no message is provided, picks the right default based on the current
 * pathname (supplier-facing pages get the partner message).
 */
export const whatsappLinkProps = (
  location: string,
  message?: string,
) => {
  const resolved =
    message ??
    (typeof window !== "undefined"
      ? getDefaultMessageForPath(window.location.pathname)
      : DEFAULT_MESSAGE);
  return {
    href: buildWhatsAppUrl(resolved),
    target: "_blank" as const,
    rel: "noopener noreferrer",
    onClick: () => trackWhatsAppClick(location),
  };
};
